import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Users, BarChart3, ArrowRight } from "lucide-react";
import { CeresMark } from "@/components/ceres/CeresMark";

const PILLARS = [
  [Users, "For every visitor", "Plain-language answers for new investors, precise comparisons and documents for advisors, dates and documents for unitholders."],
  [ShieldCheck, "Compliant by design", "Harvest-only sources with dates, as-at on every number, no personal advice, no forecasts, the standard disclaimer every time."],
  [BarChart3, "Insight for Harvest", "Every question becomes data: top questions, most-asked tickers, content gaps and Advisor Brief downloads."],
];

const AGENDA = ["Fund cards and comparisons", "Page-aware help and compliance", "French, Fund Finder and Advisor Brief", "Voice mode", "Insights dashboard"];

export default function PitchPage() {
  const navigate = useNavigate();
  const start = () => {
    ["hiq_session", "hiq_msgs"].forEach((k) => sessionStorage.removeItem(k)); // demo starts with a clean conversation
    navigate("/?presenter=1");
  };

  useEffect(() => {
    const onKey = (e) => e.key === "Enter" && !e.target.closest("a, button") && start();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main className="ceres-intro relative min-h-[100dvh] overflow-hidden text-white" data-testid="pitch-page">
      <div className="ceres-grain-field" aria-hidden="true" />
      <div className="relative mx-auto flex min-h-[100dvh] max-w-6xl flex-col px-6 py-10 sm:px-10">
        <header className="flex items-center justify-between">
          <div className="bg-white px-4 py-2"><img src="/brand/etf-header.svg" alt="Harvest ETFs" className="h-6 w-auto" /></div>
          <p className="whitespace-nowrap font-heading text-[10px] font-semibold uppercase tracking-[0.14em] text-white/70 sm:tracking-[0.2em]" data-testid="pitch-ransen">Prototype by <span className="text-white">Ransen</span></p>
        </header>

        <section className="mt-14 grid flex-1 items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <p className="hv-rise font-heading text-[12px] font-semibold uppercase tracking-[0.28em] text-hv-orange">Introducing</p>
            <div className="hv-rise mt-4 flex items-center gap-5" style={{ animationDelay: "120ms" }}>
              <CeresMark size={80} tone="dark" animate className="shrink-0" />
              <h1 className="font-heading text-5xl font-extrabold tracking-[0.1em] sm:text-7xl sm:tracking-[0.14em] lg:text-8xl">CERES</h1>
            </div>
            <p className="hv-rise mt-6 max-w-xl text-lg leading-relaxed text-white/85" style={{ animationDelay: "240ms" }}>
              Harvest's AI fund guide. It answers questions about every Harvest ETF and Harvest High Income Shares™ fund from Harvest's own content, with sources and as-at dates, in English and French, by text or voice.
            </p>
            <div className="hv-rise mt-10 flex flex-wrap gap-4" style={{ animationDelay: "360ms" }}>
              <button type="button" onClick={start} className="hv-btn-dark inline-flex items-center gap-2" data-testid="pitch-start-demo-button">
                Start guided demo <ArrowRight size={16} aria-hidden="true" />
              </button>
              <Link to="/chat" className="hv-btn-dark" data-testid="pitch-open-ceres-button">Open Ceres</Link>
              <a href="/Ceres-Feature-Rationale.pdf" target="_blank" rel="noopener noreferrer" className="hv-btn-dark" data-testid="pitch-feature-guide-link">Feature guide (PDF)</a>
            </div>
            <p className="mt-4 text-[12px] text-white/50">Press Enter to start the guided demo.</p>
          </div>

          <div className="hv-rise border border-white/15 bg-white/5 p-6 backdrop-blur-md" style={{ animationDelay: "480ms" }}>
            <h2 className="font-heading text-[12px] font-semibold uppercase tracking-[0.22em] text-hv-orange">Today's demo</h2>
            <ol className="mt-4 space-y-3" data-testid="pitch-agenda">
              {AGENDA.map((a, n) => (
                <li key={a} className="flex items-center gap-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center border border-white/40 font-heading text-[13px] font-bold">{n + 1}</span>
                  <span className="text-[15px]">{a}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="mt-14 grid gap-6 border-t border-white/15 pt-8 md:grid-cols-3" data-testid="pitch-pillars">
          {PILLARS.map(([Icon, t, d], n) => (
            <div key={t} className="hv-rise" style={{ animationDelay: `${600 + n * 120}ms` }}>
              <span className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-white/70"><Icon size={18} strokeWidth={2} aria-hidden="true" /></span>
              <h3 className="mt-3 font-heading text-[15px] font-bold">{t}</h3>
              <p className="mt-1 text-[13.5px] leading-relaxed text-white/75">{d}</p>
            </div>
          ))}
        </section>

        <footer className="mt-10 text-[11px] text-white/45">
          Prepared for Harvest Portfolios Group Inc. Pitch prototype for discussion; fund data and content sourced from harvestportfolios.com. Not investment advice.
        </footer>
      </div>
    </main>
  );
}
